<template>
  <div class="manage-panel">
    <div class="manage-panel__header">
      <span class="manage-panel__title">{{ lang.graphs }}</span>
      <div class="manage-panel__header-actions d-flex align-center">
        <v-progress-circular
          v-if="this.fetching"
          :indeterminate="true"
          :rotate="-90"
          :size="24"
          :width="2"
          color="primary"
          class="mr-4"
        >
        </v-progress-circular>
        <v-progress-circular
          v-else-if="dataFrom == 'last-5-minutes'"
          :indeterminate="this.fetching"
          :rotate="-90"
          :size="24"
          :width="2"
          :value="refreshIn * 20"
          color="primary"
          class="mr-4"
        >
          <span class="font-size-sm" v-if="!this.fetching">{{
            refreshIn
          }}</span>
        </v-progress-circular>
        <v-select
          :items="dataFromOptions"
          v-model="dataFrom"
          hide-details
          outlined
          density="compact"
          dense
          style="max-width: 120px"
        ></v-select>
      </div>
    </div>
    <div class="manage-panel__body manage-panel__body--graphs">
      <v-card-title class="pa-0">
        <v-tabs v-model="activeTab" center-active>
          <v-tab>{{ lang.cpuUsage }}</v-tab>
          <v-tab>{{ lang.diskThroughput }}</v-tab>
          <v-tab>{{ lang.diskIops }}</v-tab>
          <v-tab>{{ lang.networkTraffic }}</v-tab>
          <v-tab>{{ lang.networkPps }}</v-tab>
        </v-tabs>
      </v-card-title>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <CpuPercentGraph
            :metrics="metrics"
            :dataFrom="dataFrom"
            :graphOptions="graphOptions"
            class="graph-cpu-percent"
          />
        </v-tab-item>
        <v-tab-item>
          <DiskThroughputGraph
            :metrics="metrics"
            :dataFrom="dataFrom"
            :graphOptions="graphOptions"
            class="graph-disk-throughput"
          />
        </v-tab-item>
        <v-tab-item>
          <DiskIopsGraph
            :metrics="metrics"
            :dataFrom="dataFrom"
            :graphOptions="graphOptions"
            class="graph-disk-iops"
          />
        </v-tab-item>
        <v-tab-item>
          <NetworkTrafficGraph
            :metrics="metrics"
            :dataFrom="dataFrom"
            :graphOptions="graphOptions"
            class="graph-network-traffic"
          />
        </v-tab-item>
        <v-tab-item>
          <NetworkPpsGraph
            :metrics="metrics"
            :dataFrom="dataFrom"
            :graphOptions="graphOptions"
            class="graph-network-pps"
          />
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import Api from "@/apis/Api";
import CpuPercentGraph from "./CpuPercentGraph.vue";
import DiskThroughputGraph from "./DiskThroughputGraph.vue";
import DiskIopsGraph from "./DiskIopsGraph.vue";
import NetworkTrafficGraph from "./NetworkTrafficGraph.vue";
import NetworkPpsGraph from "./NetworkPpsGraph.vue";

export default {
  components: {
    CpuPercentGraph,
    DiskThroughputGraph,
    DiskIopsGraph,
    NetworkTrafficGraph,
    NetworkPpsGraph,
  },
  props: {},
  data() {
    return {
      activeTab: 0,
      metrics: null,
      fetching: false,
      dataFrom: "last-5-minutes",
      dataFromOptions: [
        { text: "1 hour", value: "last-hour" },
        { text: "12 hours", value: "last-12-hours" },
        { text: "Live", value: "last-5-minutes" },
      ],
      refreshIntervalId: null,
      refreshIn: 0,
    };
  },
  computed: {
    lang() {
      return {
        graphs: this.$t("heading.hosting.usage.graphs.title"),
        cpuUsage: this.$t("heading.hosting.usage.graphs.cpuUsage"),
        diskThroughput: this.$t("heading.hosting.usage.graphs.diskThroughput"),
        diskIops: this.$t("heading.hosting.usage.graphs.diskIops"),
        networkTraffic: this.$t("heading.hosting.usage.graphs.networkTraffic"),
        networkPps: this.$t("heading.hosting.usage.graphs.networkPps"),
      };
    },
    graphOptions() {
      return {
        stroke: { curve: "smooth", width: 2 },
        width: "100%",
        grid: {
          borderColor: "#DEDEF1",
          strokeDashArray: 9,
          borderWidth: "1px",
        },
        xaxis: {
          axisBorder: { show: false },
          categories: [],
          labels: {
            minHeight: 76,
            rotate: 0,
            hideOverlappingLabels: true,
            style: {
              colors: "#797997",
              fontSize: "12px",
              lineHeight: "16px",
              fontWeight: "700",
            },
          },
          convertedCatToNumeric: false,
        },
        yaxis: {
          axisBorder: { show: false },
          labels: {
            formatter: function (value) {
              if (value === null) {
                return "No Data";
              }
              return value.toFixed(2);
            },
            style: {
              colors: "#797997",
              fontSize: "12px",
              lineHeight: "16px",
              fontWeight: "700",
            },
          },
          tickAmount: 5,
        },
        chart: {
          width: "100%",
          toolbar: { show: true },
          animations: { enabled: false },
        },
        dataLabels: { enabled: false },
        markers: { size: 0 },
        legend: { show: true },
      };
    },
  },
  mounted() {
    if (!this.fetching) {
      this.loadMetrics();
      this.handleAutoRefresh();
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  watch: {
    dataFrom() {
      this.loadMetrics();
      this.handleAutoRefresh();
    },
  },
  methods: {
    loadMetrics() {
      this.fetching = true;
      Api.get(`/server/metrics/${this.dataFrom}`)
        .then((response) => {
          this.metrics = response.data.data;
        })
        .catch((error) => {
          this.$root.$emit("alert-error", Api.parseErrorMessage(error));
        })
        .finally(() => {
          this.fetching = false;
          if (this.dataFrom == "last-5-minutes") {
            this.refreshIn = 5;
          }
        });
    },
    handleAutoRefresh() {
      if (this.dataFrom == "last-5-minutes") {
        if (this.refreshIntervalId !== null) {
          return;
        }
        this.refreshIntervalId = setInterval(() => {
          if (!this.fetching) {
            this.refreshIn--;
          }
          if (this.refreshIn === 0) {
            this.loadMetrics();
          }
        }, 1000);
        return;
      }
      if (this.refreshIntervalId) {
        clearInterval(this.refreshIntervalId);
        this.refreshIntervalId = null;
      }
    },
    stopAutoRefresh() {
      if (this.refreshIntervalId) {
        clearInterval(this.refreshIntervalId);
        this.refreshIntervalId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./ServerManage.scss"></style>
<style lang="scss">
.graph-disk-iops {
  .apexcharts-canvas {
    .apexcharts-graphical {
      transform: translate(90px, 28px);
    }
  }
}
.graph-disk-throughput {
  .apexcharts-canvas {
    .apexcharts-graphical {
      transform: translate(124px, 30px);
    }
  }
}
.graph-network-traffic {
  .apexcharts-canvas {
    .apexcharts-graphical {
      transform: translate(116px, 28px);
    }
  }
}
.graph-network-pps {
  .apexcharts-canvas {
    .apexcharts-graphical {
      transform: translate(94px, 28px);
    }
  }
}
.graph-cpu-percent {
  .apexcharts-canvas {
    .apexcharts-graphical {
      transform: translate(90px, 28px);
    }
  }
}
</style>